/**
 * The Router (SPEC §4.6, architecture §7): keeps the address bar and the open
 * level in step, through a History adapter so it's testable without a browser.
 *
 * - Arriving: the URL picks the level (the startup level at the site root).
 *   Unknown levels log an error and fall back to the startup level (A-26).
 * - Opening a level any other way pushes a history entry; Back/Forward reopen
 *   levels with `origin: 'router'`, which never pushes (R-6).
 *
 * Selection and camera never reach the address: a level always opens the way
 * it's authored (its default selection and startup camera).
 */
import type { CommandBus } from "../../commands/CommandBus";
import type { CommandOrigin } from "../../commands/types";
import type { Unsubscribe } from "../../core/Emitter";
import type { Log } from "../../core/Log";
import type { ReadableStore } from "../../core/store";
import type { WorldReader } from "../../world/World";
import type { LoadProgress } from "../levels/LevelService";
import { formatRoute, parseLocation, withForeignParams } from "./route";
import type { Route } from "./route";

/** The slice of the browser History API the router needs. */
export interface HistoryPort {
  readonly pathname: string;
  readonly search: string;
  push(url: string): void;
  replace(url: string): void;
  onPopState(listener: () => void): Unsubscribe;
}

export interface RouterOptions {
  readonly history: HistoryPort;
  /** The deployment base path, e.g. "/" or "/portfolio/". */
  readonly base: string;
  readonly startupLevel: string;
  readonly bus: Pick<CommandBus, "executeAsync">;
  readonly world: WorldReader;
  readonly levels: {
    readonly progress: ReadableStore<LoadProgress>;
    assetPathOf(levelId: string): string | null;
  };
  readonly log: Log;
}

export class Router {
  private readonly options: RouterOptions;
  private readonly unsubscribes: Unsubscribe[] = [];
  /** The level the address currently names, so a load of that same level doesn't push again. */
  private addressedLevel: string | null = null;
  /**
   * How many levels the router itself is opening. A count, not a flag: Back
   * during a Back's load starts a second open that supersedes the first, and
   * the first ending mustn't make the second look like the editor's (which
   * would push a history entry and erase Forward).
   */
  private routing = 0;

  constructor(options: RouterOptions) {
    this.options = options;
  }

  /**
   * Opens the level the address names (the final boot step), then follows the
   * editor. Reports whether the visitor arrived by deep link (a non-startup
   * level), which suppresses the tour offer (A-25).
   */
  async start(): Promise<{ deepLinked: boolean }> {
    const { history, base, startupLevel } = this.options;
    const parsed = parseLocation(history.pathname, history.search, base);
    const route = this.resolveLevel(parsed.route);

    await this.openRoute(route, "boot");
    // A redirected or unknown address is rewritten to what actually opened.
    if (parsed.redirected || route.levelId !== parsed.route.levelId) {
      history.replace(this.currentUrl());
    }
    this.listen();

    const { levelId } = parsed.route;
    return { deepLinked: levelId !== null && levelId !== startupLevel };
  }

  dispose(): void {
    this.unsubscribes.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.unsubscribes.length = 0;
  }

  // ─── Arriving ──────────────────────────────────────────────────────────

  /** A route to a level that exists: unknown ids fall back to the startup level (A-26). */
  private resolveLevel(route: Route): Route {
    const { startupLevel, levels, log } = this.options;
    const id = route.levelId ?? startupLevel;
    if (levels.assetPathOf(id) !== null) return route;

    log.error("URL", `Level '${id}' not found`);
    return { levelId: startupLevel };
  }

  private async openRoute(route: Route, origin: CommandOrigin): Promise<void> {
    const id = route.levelId ?? this.options.startupLevel;

    this.routing++;
    try {
      const result = await this.options.bus.executeAsync({
        type: "level.open",
        payload: { id },
        origin,
      });
      if (result.ok && !result.cancelled) this.addressedLevel = id;
    } finally {
      this.routing--;
    }
  }

  // ─── Following the editor ──────────────────────────────────────────────

  private listen(): void {
    const { history, levels } = this.options;

    this.unsubscribes.push(
      history.onPopState(() => {
        void this.onPopState();
      }),
      levels.progress.subscribe(() => {
        this.onLoadProgress(levels.progress.getSnapshot());
      }),
    );
  }

  /** A level opened from the editor (Content Browser, tour, …) becomes a history entry. */
  private onLoadProgress(progress: LoadProgress): void {
    if (progress.phase !== "done" || this.routing > 0 || progress.levelId === null) return;
    if (progress.levelId === this.addressedLevel) return;

    this.addressedLevel = progress.levelId;
    const url = this.currentUrl();
    this.options.history.push(url);
    this.options.log.display("URL", `${url} pushed to history`);
  }

  private async onPopState(): Promise<void> {
    const { history, base, world } = this.options;
    const route = this.resolveLevel(parseLocation(history.pathname, history.search, base).route);
    const target = route.levelId ?? this.options.startupLevel;
    if (target === world.getSnapshot().levelId) return;

    await this.openRoute(route, "router");
  }

  /** The URL for the open level, keeping the visitor's own query parameters. */
  private currentUrl(): string {
    const { world, base, history } = this.options;
    return withForeignParams(
      formatRoute({ levelId: world.getSnapshot().levelId }, base),
      history.search,
    );
  }
}
